import { useEffect } from "react";

import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";

import { useCompany } from "@/hooks/util/useCompany";
import {
  loadCompanyDashboard,
  loadCompanyTotalDashboard,
  loadCompanyTransactions,
  selectCompanyDashboardState
} from "@/store/features/companyDashboard/companyDashboard";

interface CompanyDashboardProps {
  label: string;
  after: Date | null | string;
  before: Date | null | string;
  offset?: number;
}

export const useCompanyDashboard = ({
  before,
  after,
  offset = 0
}: CompanyDashboardProps) => {
  const { activeCompanyId, activeClientId } = useCompany();

  const dispatch = useDispatch();

  const dateFormat = "yyyy-MM-dd";

  useEffect(() => {
    if (activeCompanyId) {
      dispatch(loadCompanyTotalDashboard(activeCompanyId as string));
    }
  }, [dispatch, activeCompanyId]);

  useEffect(() => {
    // Only make the API call if we have valid dates and an active company
    if (activeCompanyId && before && after) {
      try {
        // Convert dates to Date objects if they're strings
        const beforeDate = typeof before === 'string' ? new Date(before) : before;
        const afterDate = typeof after === 'string' ? new Date(after) : after;
        
        const formattedBefore = format(beforeDate, dateFormat);
        const formattedAfter = format(afterDate, dateFormat);
        
        dispatch(
          loadCompanyDashboard(activeCompanyId as string, {
            before: formattedBefore,
            after: formattedAfter
          })
        );
      } catch (error) {
        console.error("Error formatting dates in useCompanyDashboard:", error);
      }
    }
  }, [dispatch, activeCompanyId, before, after]);

  useEffect(() => {
    // Only make the API call if we have valid dates and an active client
    if (activeClientId && before && after) {
      try {
        // Convert dates to Date objects if they're strings
        const beforeDate = typeof before === 'string' ? new Date(before) : before;
        const afterDate = typeof after === 'string' ? new Date(after) : after;
        
        const formattedBefore = format(beforeDate, dateFormat);
        const formattedAfter = format(afterDate, dateFormat);
        
        dispatch(
          loadCompanyTransactions(
            activeClientId as string,
            {
              before: formattedBefore,
              after: formattedAfter
            },
            offset
          )
        );
      } catch (error) {
        console.error("Error formatting dates in useCompanyDashboard (transactions):", error);
      }
    }
  }, [dispatch, activeCompanyId, activeClientId, before, after, offset]);

  const {
    dashboard,
    isDashboardLoading,
    totalDashboard,
    isTotalDashboardLoading,
    companyTransactions,
    isCompanyTransactionsLoading
  } = useSelector(selectCompanyDashboardState);

  return {
    dashboard,
    isDashboardLoading,
    totalDashboard,
    isTotalDashboardLoading,
    companyTransactions,
    isCompanyTransactionsLoading
  };
};
